<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';

// Injected rather than imported, the same way ColumnsBlock and the
// Classic PostBodyBlock do it: importing the registry here would be a
// circular dependency (blocks.js → this → blocks.js), and the injected
// one is template-aware, so a Trailhead post pours Trailhead blocks.
const components = inject('tiqBlockComponents', null);
function componentFor(type) { return components && components[type]; }

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const post = inject('tiqCurrentPost', null);
// A visitor never meets the slot's placeholder: with no real post
// to render it draws nothing, and only the editor keeps the stand-in.
const editing = inject('tiqEditorCanvas', false);

const blocks = computed(() => {
    if (!post) return [];
    return Array.isArray(post.body) ? post.body : [];
});
// The design's article column is 680px; the other widths step either
// side of it so the option still means something.
const widthClass = computed(() => `th-post-body__inner--${props.config.width || 'medium'}`);
</script>

<template>
    <!-- A live post with no body renders nothing rather than showing a
         reader the slot indicator; the editor canvas still gets it. -->
    <section v-if="post ? blocks.length : editing"
             :id="config.anchor_id || null"
             class="th-block th-post-body"
             :style="styleVars(config)">
        <div class="th-wrap th-sec">
            <div class="th-post-body__inner" :class="widthClass">
                <template v-for="block in blocks" :key="block.id">
                    <component v-if="componentFor(block.type)"
                               :is="componentFor(block.type)"
                               :config="block.config"
                               :accent="accent"
                               :org="org"
                               :site_data="site_data" />
                </template>
                <p v-if="!post" class="th-post-body__empty">
                    Post body appears here when the template renders a real post.
                </p>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-post-body__inner { margin: 0 auto; }
.th-post-body__inner--narrow { max-width: 560px; }
.th-post-body__inner--medium { max-width: 680px; }
.th-post-body__inner--wide { max-width: 820px; }
.th-post-body__inner--full { max-width: none; }

/* Child blocks shed their own band padding and gutter inside the
   article column, so the result reads as flowing prose rather than
   stacked sections indented twice. */
.th-post-body__inner :deep(section) {
    padding: 0 0 22px !important;
    background: transparent !important;
    min-height: 0 !important;
}
.th-post-body__inner :deep(.th-wrap) { padding-left: 0; padding-right: 0; max-width: none; }
.th-post-body__inner :deep(.th-sec),
.th-post-body__inner :deep(.th-band) { padding-top: 0; padding-bottom: 0; }

.th-post-body__empty {
    margin: 0;
    padding: 28px;
    text-align: center;
    border: 1px dashed var(--th-rule);
    border-radius: var(--r);
    font: 400 12px/1.4 var(--th-mono);
    letter-spacing: .04em;
    color: var(--th-faint);
}
</style>
